import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';

import { CustomerProfile, OrderWithContext, Menu, Restaurant, CustomerDeliveryAddress, OrderMeal, CmsLink, CmsLinkLocation, CmsLinkTarget } from '@/models';
import { HeaderFlags, CountryCode } from '@/store/ui';
import { Routes } from '@/router';
import { getBrowserProduct, shouldShowLink } from '@/helpers';
import defer from 'lodash/defer';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import { ViewBagEvent } from '@/services/analytics/events';
import { HeaderLinkWebInteractions } from '@/services/analytics/platforms/adobe-analytics/constants';

@Component({
  name: 'CmgHeaderActionsRight',
})
export default class CmgHeaderActionsRight extends Vue {
  @Prop({ default: false, type: Boolean })
  blur!: boolean;

  get isOrderingOnline(): boolean {
    return this.$services.utils.isOrderingOnline();
  }

  get links(): CmsLink[] {
    return this.$appStore.state.cms.header.links.filter(shouldShowLink(this.siteCountryCode, !!this.isCustomerLoggedIn, this.$appStore.state));
  }

  get headerOptions() {
    return this.$appStore.state.ui.header.options;
  }

  get headerFlags() {
    return this.headerOptions.flags;
  }

  get allHeaderFlags() {
    return HeaderFlags;
  }

  get bagImageUrl(): string {
    return this.$appStore.state.cms.images.icons.bag.default;
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get isCustomerLoggedIn(): boolean | undefined {
    return this.$services.customer.isLoggedIn();
  }

  get isCustomerInitialized(): boolean {
    return this.isCustomerLoggedIn !== undefined;
  }

  get showBag(): boolean {
    return this.$appStore.state.ui.bag.visible;
  }

  get showIngredientPanel(): boolean {
    return this.$appStore.state.ui.ingredientsPage.ingredientsPanelVisibility;
  }

  get selectedRestaurantAddress(): Restaurant | null {
    const restaurant = this.$appStore.state.restaurant.selectedRestaurant;
    // If the last restaurant doesn't match the current country experience don't display anything in the FAC pill
    if (restaurant && restaurant.address.countryCode && this.$appStore.state.ui.countryProfile.countryCode.toLowerCase() !== restaurant.address.countryCode.toLowerCase()) {
      return null;
    }

    return restaurant;
  }

  get selectedDeliveryAddress(): CustomerDeliveryAddress | null {
    const selectedDeliveryAddress = this.$appStore.state.order.selectedDeliveryAddress;
    // If the last restaurant doesn't match the current country experience don't display anything in the FAC pill
    if (
      selectedDeliveryAddress &&
      selectedDeliveryAddress.countryCode &&
      this.$appStore.state.ui.countryProfile.countryCode.toLowerCase() !== selectedDeliveryAddress.countryCode.toLowerCase()
    ) {
      return null;
    }

    return selectedDeliveryAddress;
  }

  get order(): OrderWithContext | null {
    return this.$services.order.getCurrentOrder();
  }

  get meals(): OrderMeal[] {
    if (this.order && this.order.order.meals) {
      const upsellItems = this.$services.config.getUpsellItems();
      if (this.order?.order?.meals.length > 0 && (!upsellItems || upsellItems.length === 0)) {
        this.$services.config.setUpsellItems();
      }
    }
    return (this.order && this.order.order.meals) || [];
  }

  get menu(): Menu | null {
    return this.$appStore.state.menu.activeMenu;
  }

  get isIE(): boolean {
    return getBrowserProduct(navigator) === 'IE';
  }

  get profileCountryCode(): string {
    return this.customer ? this.customer.country || CountryCode.US : '';
  }

  get siteCountryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get numberOfMealsWithoutUpsell() {
    const upsellMeal = this.$appStore.state.order.upsellMeal;
    if (upsellMeal) {
      // If an upsell meal is the only meal in the bag, we will show a count of 1
      return this.meals.filter(meal => meal.mealId !== upsellMeal.mealId).length || 1;
    }
    return this.meals.length;
  }

  onClickBagIcon(event: any) {
    this.$services.bag.toggleBagVisibility();
    const fullmodalExist = document.querySelectorAll('.fullModal').length;
    if (fullmodalExist <= 1) {
      this.$services.ui.pushEventTargetAsLastSelectedElement(event);
      this.$nextTick(() => defer(() => this.$services.ui.focusDiv('.fullModal')));
    }

    if (this.order) {
      this.$services.analytics.send(new ViewBagEvent(this.meals));
    }
    this.$services.analytics.addLinkClickedEventToStore(HeaderLinkWebInteractions.Bag);
  }

  async onClickSignIn(event: any) {
    //fetch sign-in modal CF if clicked from header
    if (!this.$services.config.getSignInModal()?.isModalEnabled || Object.keys(this.$services.config.getSignInModal()).length === 0) {
      await this.$services.config.setSignInModal()
      await this.$services.config.setLegalText();
      
    }
    // If on sign-in page or create-account page, don't open
    // the modal.
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    if (this.$router.currentRoute.name == Routes.SignIn || this.$router.currentRoute.name == Routes.CreateAccount) {
      this.$router.push({ name: Routes.SignIn });
    } else {
      this.$services.ui.setModalVisibility('signIn', true);
      this.$services.ui.firstFocus();
    }

    this.$services.analytics.addLinkClickedEventToStore(HeaderLinkWebInteractions.SignIn);

  }

  async showAccountMenu(event: any) {
    await this.$services.config.setLegalText();
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('account', true);
    Vue.nextTick(() => this.$services.ui.focusDiv('.modal-content'));

    this.$services.analytics.addLinkClickedEventToStore(HeaderLinkWebInteractions.Hey);
  }

  showFindAChipotleModal(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('findAChipotle', true);
    this.$services.ui.firstFocus();

    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.Behavior,
      action: AnalyticsEventAction.Click,
      label: AnalyticsEventLabel.GlobalHeaderPickupDelivery,
    });

    this.$services.analytics.addLinkClickedEventToStore(HeaderLinkWebInteractions.FAC);
  }
}
